import { getRoleDisplayName } from '@apps/web/lib/caregiver_role_utils';
import {
    CAREGIVER_ROLES,
    type CaregiverRole,
} from '@libraries/typescript/api_client';

export type RecommendationAvailabilitySource =
    | 'manual'
    | 'phoebe_gathered'
    | 'external';

export type RecommendationDefaultFilterMatchMode = 'any' | 'all';

export type RecommendationAvailabilityPreference =
    | 'available'
    | 'partial'
    | 'unavailable'
    | 'unknown';

export type RecommendationScheduleAvailability =
    | 'available'
    | 'has_shift_conflict';

export type RecommendationDefaultFilterColumnId =
    | 'availability_preference'
    | 'schedule_availability'
    | 'primary_language'
    | 'roles'
    | 'overtime_status'
    | 'postal_code'
    | 'office_name';

export type RecommendationDefaultColumnFilterValue = {
    readonly include?: Array<string>;
    readonly exclude?: Array<string>;
    readonly include_mode: RecommendationDefaultFilterMatchMode;
    readonly exclude_mode: RecommendationDefaultFilterMatchMode;
};

export type RecommendationTableFilterValue = {
    readonly include: Array<string>;
    readonly exclude: Array<string>;
    readonly includeMode: RecommendationDefaultFilterMatchMode;
    readonly excludeMode: RecommendationDefaultFilterMatchMode;
};

export type RecommendationTableColumnFilter = {
    readonly id: string;
    readonly value: RecommendationTableFilterValue;
};

export type RecommendationDefaults = {
    readonly max_distance_mi: number | null;
    readonly max_weekly_hours: number | null;
    readonly max_daily_hours: number | null;
    readonly max_consecutive_days: number | null;
    readonly overtime_behavior: 'hide' | 'flag';
    readonly commute_time_min: number;
    readonly availability_source_priority: Array<RecommendationAvailabilitySource>;
    readonly default_column_filters: Partial<
        Record<
            RecommendationDefaultFilterColumnId,
            RecommendationDefaultColumnFilterValue
        >
    >;
    readonly match_patient_tags: boolean;
    readonly match_patient_office: boolean;
    readonly ignored_patient_tags: Array<string>;
    readonly max_contact_attempts_48h: number | null;
};

export type RecommendationDefaultFilterOptions = Record<
    RecommendationDefaultFilterColumnId,
    Array<string>
>;

export type RecommendationDefaultOptions = {
    readonly filterOptions: RecommendationDefaultFilterOptions;
    readonly patientTags: Array<string>;
};

export type RecommendationDefaultOption = {
    readonly value: string;
    readonly label: string;
};

export type RecommendationDefaultFilterConfig = {
    readonly id: RecommendationDefaultFilterColumnId;
    readonly label: string;
    readonly helperText: string;
    readonly supportsArrayModes?: boolean;
};

export const RECOMMENDATION_DEFAULT_FILTER_COLUMNS: ReadonlyArray<RecommendationDefaultFilterColumnId> =
    [
        'availability_preference',
        'schedule_availability',
        'primary_language',
        'roles',
        'overtime_status',
        'postal_code',
        'office_name',
    ];

export const RECOMMENDATION_DEFAULT_FILTER_CONFIGS: ReadonlyArray<RecommendationDefaultFilterConfig> =
    [
        {
            id: 'overtime_status',
            label: 'OT Status',
            helperText:
                'Controls whether overtime caregivers appear in recommendations.',
        },
        {
            id: 'availability_preference',
            label: 'Availability Preference',
            helperText:
                'Based on what the caregiver said they\u2019re available for, not their assigned shifts.',
        },
        {
            id: 'schedule_availability',
            label: 'Schedule Availability',
            helperText:
                'Based on the caregiver\u2019s actual assigned shifts, not their preferences.',
        },
        {
            id: 'primary_language',
            label: 'Language',
            helperText: 'Pre-filter by caregiver language.',
        },
        {
            id: 'roles',
            label: 'License',
            helperText: 'Pre-filter by caregiver license type.',
            supportsArrayModes: true,
        },
        {
            id: 'postal_code',
            label: 'Zip code',
            helperText: 'Pre-filter by caregiver ZIP code.',
        },
        {
            id: 'office_name',
            label: 'Office',
            helperText: 'Pre-filter by caregiver office.',
        },
    ];

export const SOURCE_LABELS: Record<RecommendationAvailabilitySource, string> = {
    manual: 'Manual',
    phoebe_gathered: 'Phoebe Gathered',
    external: 'External (EHR)',
};

export const DEFAULT_RECOMMENDATION_DEFAULTS: RecommendationDefaults = {
    max_distance_mi: null,
    max_weekly_hours: 40,
    max_daily_hours: null,
    max_consecutive_days: null,
    overtime_behavior: 'flag',
    commute_time_min: 30,
    availability_source_priority: ['manual', 'phoebe_gathered', 'external'],
    default_column_filters: {},
    match_patient_tags: true,
    match_patient_office: true,
    ignored_patient_tags: [],
    max_contact_attempts_48h: null,
};

const BASELINE_RECOMMENDATION_FILTERS = {
    availability_preference: {
        include: [],
        exclude: ['unavailable'],
        includeMode: 'any',
        excludeMode: 'all',
    },
    schedule_availability: {
        include: [],
        exclude: ['has_shift_conflict'],
        includeMode: 'any',
        excludeMode: 'all',
    },
} satisfies Record<
    'availability_preference' | 'schedule_availability',
    RecommendationTableFilterValue
>;

function isLegacyAvailabilityFilterColumnId(
    value: string,
): value is 'availability' {
    return value === 'availability';
}

const CANONICAL_AVAILABILITY_PREFERENCE_LABELS: Record<
    RecommendationAvailabilityPreference,
    string
> = {
    available: 'Available',
    partial: 'Partial',
    unavailable: 'Unavailable',
    unknown: 'Unknown',
};
const CANONICAL_SCHEDULE_AVAILABILITY_LABELS: Record<
    RecommendationScheduleAvailability,
    string
> = {
    available: 'Available for Shift',
    has_shift_conflict: 'Has Assigned-Shift Conflict',
};

const CANONICAL_OVERTIME_STATUS_LABELS: Record<string, string> = {
    within_limit: 'Within Limit',
    overtime: 'Overtime',
};

const LANGUAGE_NAMES: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    zh: 'Chinese',
    'zh-CN': 'Chinese (Simplified)',
    'zh-TW': 'Chinese (Traditional)',
    tl: 'Tagalog',
    vi: 'Vietnamese',
    ar: 'Arabic',
    fr: 'French',
    ko: 'Korean',
    ru: 'Russian',
    de: 'German',
    pt: 'Portuguese',
    ja: 'Japanese',
    hi: 'Hindi',
    it: 'Italian',
    pl: 'Polish',
    uk: 'Ukrainian',
    fa: 'Persian',
    bn: 'Bengali',
    ht: 'Haitian Creole',
    el: 'Greek',
    he: 'Hebrew',
    th: 'Thai',
    ur: 'Urdu',
    pa: 'Punjabi',
    gu: 'Gujarati',
    ta: 'Tamil',
    te: 'Telugu',
    mr: 'Marathi',
    ml: 'Malayalam',
    kn: 'Kannada',
    or: 'Odia',
    my: 'Burmese',
    km: 'Khmer',
    lo: 'Lao',
    am: 'Amharic',
    so: 'Somali',
    sw: 'Swahili',
    yo: 'Yoruba',
    ig: 'Igbo',
    ha: 'Hausa',
    zu: 'Zulu',
    af: 'Afrikaans',
    nl: 'Dutch',
    sv: 'Swedish',
    no: 'Norwegian',
    da: 'Danish',
    fi: 'Finnish',
    cs: 'Czech',
    sk: 'Slovak',
    hu: 'Hungarian',
    ro: 'Romanian',
    bg: 'Bulgarian',
    sr: 'Serbian',
    hr: 'Croatian',
    sl: 'Slovenian',
    lt: 'Lithuanian',
    lv: 'Latvian',
    et: 'Estonian',
    tr: 'Turkish',
    az: 'Azerbaijani',
    ka: 'Georgian',
    hy: 'Armenian',
    ne: 'Nepali',
    si: 'Sinhala',
    ms: 'Malay',
    id: 'Indonesian',
    fil: 'Filipino',
    ceb: 'Cebuano',
    hmn: 'Hmong',
    ilo: 'Ilocano',
};

const STATIC_RECOMMENDATION_DEFAULT_OPTIONS = {
    availability_preference: [
        'available',
        'partial_conflict',
        'unavailable',
        'unknown',
    ],
    schedule_availability: ['available', 'has_shift_conflict'],
    overtime_status: ['within_limit', 'overtime'],
} satisfies Pick<
    RecommendationDefaultFilterOptions,
    'availability_preference' | 'schedule_availability' | 'overtime_status'
>;

export function getRecommendationDefaultPredefinedOptions(
    columnId: RecommendationDefaultFilterColumnId,
): Array<string> {
    if (
        columnId === 'availability_preference' ||
        columnId === 'schedule_availability' ||
        columnId === 'overtime_status'
    ) {
        return [...STATIC_RECOMMENDATION_DEFAULT_OPTIONS[columnId]];
    }
    return [];
}

export function normalizeRecommendationDefaultValues(
    values: ReadonlyArray<string> | undefined | null,
): Array<string> {
    const seen = new Set<string>();
    const normalized: Array<string> = [];

    for (const value of values ?? []) {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            continue;
        }
        const key = trimmed.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        normalized.push(trimmed);
    }

    return normalized;
}

function sortOptionValues(
    columnId: RecommendationDefaultFilterColumnId,
    values: ReadonlyArray<string>,
): Array<string> {
    return [...values].sort((left, right) => {
        if (columnId === 'availability_preference') {
            const order =
                STATIC_RECOMMENDATION_DEFAULT_OPTIONS.availability_preference;
            return order.indexOf(left) - order.indexOf(right);
        }
        if (columnId === 'schedule_availability') {
            const order =
                STATIC_RECOMMENDATION_DEFAULT_OPTIONS.schedule_availability;
            return order.indexOf(left) - order.indexOf(right);
        }
        if (columnId === 'overtime_status') {
            const order = STATIC_RECOMMENDATION_DEFAULT_OPTIONS.overtime_status;
            return order.indexOf(left) - order.indexOf(right);
        }
        return formatRecommendationDefaultOptionLabel(
            columnId,
            left,
        ).localeCompare(
            formatRecommendationDefaultOptionLabel(columnId, right),
            undefined,
            { numeric: true, sensitivity: 'base' },
        );
    });
}

export function isRecommendationDefaultFilterColumnId(
    value: string,
): value is RecommendationDefaultFilterColumnId {
    return RECOMMENDATION_DEFAULT_FILTER_COLUMNS.includes(
        value as RecommendationDefaultFilterColumnId,
    );
}

export function formatRecommendationDefaultOptionLabel(
    columnId: RecommendationDefaultFilterColumnId,
    value: string,
): string {
    switch (columnId) {
        case 'availability_preference':
            if (value === 'partial_conflict') {
                return 'Partial conflict';
            }
            return (
                CANONICAL_AVAILABILITY_PREFERENCE_LABELS[
                    value as RecommendationAvailabilityPreference
                ] ?? value
            );
        case 'schedule_availability':
            return (
                CANONICAL_SCHEDULE_AVAILABILITY_LABELS[
                    value as RecommendationScheduleAvailability
                ] ?? value
            );
        case 'primary_language': {
            const normalized = value.toLowerCase().trim();
            return LANGUAGE_NAMES[normalized] ?? LANGUAGE_NAMES[value] ?? value;
        }
        case 'roles':
            return CAREGIVER_ROLES.includes(value as CaregiverRole)
                ? getRoleDisplayName(value as CaregiverRole)
                : value;
        case 'overtime_status':
            return CANONICAL_OVERTIME_STATUS_LABELS[value] ?? value;
        case 'postal_code':
        case 'office_name':
            return value;
        default:
            return value;
    }
}

export function normalizeRecommendationDefaultColumnFilterValue(
    value: RecommendationDefaultColumnFilterValue | null | undefined,
): RecommendationDefaultColumnFilterValue {
    return {
        include: normalizeRecommendationDefaultValues(value?.include),
        exclude: normalizeRecommendationDefaultValues(value?.exclude),
        include_mode: value?.include_mode === 'all' ? 'all' : 'any',
        exclude_mode: value?.exclude_mode === 'any' ? 'any' : 'all',
    };
}

function toRecommendationTableFilterValue(
    value: RecommendationDefaultColumnFilterValue | null | undefined,
): RecommendationTableFilterValue {
    const normalized = normalizeRecommendationDefaultColumnFilterValue(value);
    return {
        include: normalized.include ?? [],
        exclude: normalized.exclude ?? [],
        includeMode: normalized.include_mode,
        excludeMode: normalized.exclude_mode,
    };
}

function hasFilterSelections(value: RecommendationTableFilterValue): boolean {
    return value.include.length > 0 || value.exclude.length > 0;
}

function getDefaultOptionResponseValues(
    record: Record<string, unknown>,
    columnId: RecommendationDefaultFilterColumnId,
): Array<string> {
    const rawValues =
        columnId === 'availability_preference'
            ? (record['availability_preference'] ?? record['availability'])
            : record[columnId];

    if (!Array.isArray(rawValues)) {
        return [];
    }

    return rawValues.map((value) => String(value));
}

export function normalizeRecommendationDefaults(
    defaults: RecommendationDefaults | null | undefined,
): RecommendationDefaults {
    if (!defaults) {
        return DEFAULT_RECOMMENDATION_DEFAULTS;
    }

    const rawFilters = (defaults.default_column_filters ?? {}) as Record<
        string,
        RecommendationDefaultColumnFilterValue | null | undefined
    >;
    const normalizedFiltersById = new Map<
        RecommendationDefaultFilterColumnId,
        RecommendationDefaultColumnFilterValue
    >();

    for (const [columnId, value] of Object.entries(rawFilters)) {
        const canonicalColumnId = isLegacyAvailabilityFilterColumnId(columnId)
            ? 'availability_preference'
            : columnId;
        if (!isRecommendationDefaultFilterColumnId(canonicalColumnId)) {
            continue;
        }
        if (
            isLegacyAvailabilityFilterColumnId(columnId) &&
            rawFilters['availability_preference'] !== undefined
        ) {
            continue;
        }

        normalizedFiltersById.set(
            canonicalColumnId,
            normalizeRecommendationDefaultColumnFilterValue(value),
        );
    }

    return {
        max_distance_mi:
            defaults.max_distance_mi === undefined
                ? DEFAULT_RECOMMENDATION_DEFAULTS.max_distance_mi
                : defaults.max_distance_mi,
        max_weekly_hours:
            defaults.max_weekly_hours === undefined
                ? DEFAULT_RECOMMENDATION_DEFAULTS.max_weekly_hours
                : defaults.max_weekly_hours,
        max_daily_hours:
            defaults.max_daily_hours === undefined
                ? DEFAULT_RECOMMENDATION_DEFAULTS.max_daily_hours
                : defaults.max_daily_hours,
        max_consecutive_days:
            defaults.max_consecutive_days === undefined
                ? DEFAULT_RECOMMENDATION_DEFAULTS.max_consecutive_days
                : defaults.max_consecutive_days,
        overtime_behavior:
            defaults.overtime_behavior ??
            DEFAULT_RECOMMENDATION_DEFAULTS.overtime_behavior,
        commute_time_min:
            defaults.commute_time_min ??
            DEFAULT_RECOMMENDATION_DEFAULTS.commute_time_min,
        availability_source_priority:
            (defaults.availability_source_priority?.length ?? 0) > 0
                ? [...defaults.availability_source_priority]
                : [
                      ...DEFAULT_RECOMMENDATION_DEFAULTS.availability_source_priority,
                  ],
        default_column_filters: Object.fromEntries(
            normalizedFiltersById.entries(),
        ) as RecommendationDefaults['default_column_filters'],
        match_patient_tags:
            defaults.match_patient_tags ??
            DEFAULT_RECOMMENDATION_DEFAULTS.match_patient_tags,
        match_patient_office:
            defaults.match_patient_office ??
            DEFAULT_RECOMMENDATION_DEFAULTS.match_patient_office,
        ignored_patient_tags: normalizeRecommendationDefaultValues(
            defaults.ignored_patient_tags,
        ),
        max_contact_attempts_48h:
            defaults.max_contact_attempts_48h === undefined
                ? DEFAULT_RECOMMENDATION_DEFAULTS.max_contact_attempts_48h
                : defaults.max_contact_attempts_48h,
    };
}

export function normalizeRecommendationDefaultOptionsResponse(
    response: unknown,
): RecommendationDefaultOptions {
    const record =
        response && typeof response === 'object'
            ? (response as Record<string, unknown>)
            : {};

    const filterOptions = Object.fromEntries(
        RECOMMENDATION_DEFAULT_FILTER_COLUMNS.map((columnId) => {
            const normalized = normalizeRecommendationDefaultValues(
                getDefaultOptionResponseValues(record, columnId),
            );
            return [columnId, sortOptionValues(columnId, normalized)];
        }),
    ) as RecommendationDefaultFilterOptions;

    return {
        filterOptions,
        patientTags: normalizeRecommendationDefaultValues(
            Array.isArray(record['patient_tags'])
                ? record['patient_tags'].map((value) => String(value))
                : [],
        ),
    };
}

export function buildRecommendationDefaultOptionCatalog(params: {
    readonly options: RecommendationDefaultFilterOptions | null | undefined;
    readonly currentFilters:
        | Partial<
              Record<
                  RecommendationDefaultFilterColumnId,
                  RecommendationDefaultColumnFilterValue
              >
          >
        | null
        | undefined;
}): Record<
    RecommendationDefaultFilterColumnId,
    Array<RecommendationDefaultOption>
> {
    const catalog = {} as Record<
        RecommendationDefaultFilterColumnId,
        Array<RecommendationDefaultOption>
    >;

    for (const columnId of RECOMMENDATION_DEFAULT_FILTER_COLUMNS) {
        const optionSet = new Set<string>();

        for (const value of getRecommendationDefaultPredefinedOptions(
            columnId,
        )) {
            optionSet.add(value);
        }

        for (const value of params.options?.[columnId] ?? []) {
            optionSet.add(value);
        }

        const filterValue = params.currentFilters?.[columnId];
        for (const value of filterValue?.include ?? []) {
            optionSet.add(value);
        }
        for (const value of filterValue?.exclude ?? []) {
            optionSet.add(value);
        }

        const values = sortOptionValues(columnId, [...optionSet]);
        catalog[columnId] = values.map((value) => ({
            value,
            label: formatRecommendationDefaultOptionLabel(columnId, value),
        }));
    }

    return catalog;
}

export function getEffectiveRecommendationPatientTags(params: {
    readonly patientTags: ReadonlyArray<string> | undefined | null;
    readonly ignoredPatientTags: ReadonlyArray<string> | undefined | null;
}): Array<string> {
    const patientTags = normalizeRecommendationDefaultValues(
        params.patientTags,
    );
    if (patientTags.length === 0) {
        return [];
    }

    const ignoredTagKeys = new Set(
        normalizeRecommendationDefaultValues(params.ignoredPatientTags).map(
            (tag) => tag.toLowerCase(),
        ),
    );

    return patientTags.filter((tag) => !ignoredTagKeys.has(tag.toLowerCase()));
}

export function buildRecommendationInitialColumnFilters(params: {
    readonly defaults: RecommendationDefaults | null | undefined;
    readonly patientTags?: ReadonlyArray<string>;
    readonly patientOfficeName?: string | null;
    readonly defaultTagFilters?: ReadonlyArray<string>;
}): Array<RecommendationTableColumnFilter> {
    const filtersById = new Map<string, RecommendationTableColumnFilter>();

    for (const [columnId, value] of Object.entries(
        BASELINE_RECOMMENDATION_FILTERS,
    )) {
        filtersById.set(columnId, {
            id: columnId,
            value: {
                ...value,
                include: [...value.include],
                exclude: [...value.exclude],
            },
        });
    }

    const hasSavedDefaults =
        params.defaults !== null && params.defaults !== undefined;
    const defaults = normalizeRecommendationDefaults(params.defaults);

    if (hasSavedDefaults) {
        for (const columnId of RECOMMENDATION_DEFAULT_FILTER_COLUMNS) {
            const filterValue = defaults.default_column_filters[columnId];
            if (!filterValue) {
                continue;
            }

            const tableValue = toRecommendationTableFilterValue(filterValue);
            if (!hasFilterSelections(tableValue)) {
                continue;
            }

            filtersById.set(columnId, {
                id: columnId,
                value: tableValue,
            });
        }
    } else {
        const legacyTagFilters = normalizeRecommendationDefaultValues(
            params.defaultTagFilters,
        );
        if (legacyTagFilters.length > 0) {
            filtersById.set('caregiver_tags', {
                id: 'caregiver_tags',
                value: {
                    include: legacyTagFilters,
                    exclude: [],
                    includeMode: 'all',
                    excludeMode: 'all',
                },
            });
        }
    }

    const patientTags = getEffectiveRecommendationPatientTags({
        patientTags: params.patientTags,
        ignoredPatientTags: defaults.ignored_patient_tags,
    });
    const patientOfficeName = params.patientOfficeName;
    if (
        hasSavedDefaults &&
        defaults.match_patient_tags &&
        patientTags.length > 0
    ) {
        filtersById.set('caregiver_tags', {
            id: 'caregiver_tags',
            value: {
                include: patientTags,
                exclude: [],
                includeMode: 'all',
                excludeMode: 'all',
            },
        });
    }
    if (
        hasSavedDefaults &&
        defaults.match_patient_office &&
        patientOfficeName
    ) {
        filtersById.set('office_name', {
            id: 'office_name',
            value: {
                include: [patientOfficeName],
                exclude: [],
                includeMode: 'any',
                excludeMode: 'all',
            },
        });
    }

    return [...filtersById.values()]
        .filter((filter) => hasFilterSelections(filter.value))
        .sort((left, right) => left.id.localeCompare(right.id));
}

export function buildRecommendationDefaultEditorFilters(
    defaults: RecommendationDefaults | null | undefined,
): Record<RecommendationDefaultFilterColumnId, RecommendationTableFilterValue> {
    const filters = RECOMMENDATION_DEFAULT_FILTER_COLUMNS.reduce<
        Record<
            RecommendationDefaultFilterColumnId,
            RecommendationTableFilterValue
        >
    >(
        (accumulator, columnId) => {
            accumulator[columnId] = {
                include: [],
                exclude: [],
                includeMode: 'any',
                excludeMode: 'all',
            };
            return accumulator;
        },
        {} as Record<
            RecommendationDefaultFilterColumnId,
            RecommendationTableFilterValue
        >,
    );

    for (const filter of buildRecommendationInitialColumnFilters({
        defaults,
        patientTags: [],
        defaultTagFilters: [],
    })) {
        if (!isRecommendationDefaultFilterColumnId(filter.id)) {
            continue;
        }

        filters[filter.id] = {
            include: [...filter.value.include],
            exclude: [...filter.value.exclude],
            includeMode: filter.value.includeMode,
            excludeMode: filter.value.excludeMode,
        };
    }

    return filters;
}
